/* eslint-disable react-hooks/exhaustive-deps */
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelectorToStore } from '@hooks/reduxHooks';
import useTTUStore from '@stores/ttu';
import { getTTUIsGoalEnabled } from '@stores/selectors/ttuSelectors';
import Dynamic from '@base/Dynamic/Dynamic';
import CarouselFeedBlock from '@cont/FeedTypeBlock/CarouselFeedBlock';
import { toggleEnrolledCourse } from '@actions/courses';
import { GOAL_PAGE } from '@constants/appFlyerLinks';
import { mediaQuery, Spacings } from '@styles/index';
import LearnOnTheGo from '@comp/LearnOnTheGo/LearnOnTheGo';
import TTUBanner from '@cont/TalkToUnacademy/Banner';
import useWindowSize from '@hooks/useWindowSize';
import AppInstallBanner from '@cont/Homepage/AppInstallBanner';
import PlatformTTU from '@cont/TalkToUnacademy/PlatformTTU';
import { bannerStates } from '@constants/index';
import { getIsSubscribed } from '@selectors/subscription';
import isEmpty from 'lodash/isEmpty';
import useDst from '@api/hooks/goal/test-series/useDST';
import {
  BATCH_GROUP_TYPE,
  DEFAULT_QUERY_TYPE
} from '@cont/BatchGroup/constants';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import useOneLinkSmartScript from '@hooks/useOneLinkSmartScript';
import CompeteLaunchBanner from '@cont/Compete/CompeteLaunchBanner';
import useCompeteConfig from '@cont/Compete/useCompeteConfig';
import { COMPETE_STATES } from '@cont/Compete/constants';
import IconicSection from './Iconic/IconicSection';
import SecondSection from './SecondSection';
import LiteSection from './LiteSection';
import AchieversCard from './AchieversCard';
import NewAchieversCard from './NewAchieversCard';
import PageHead from './PageHead';
import K12PageHead from './K12PageHead';
import FourthSection from './FourthSection';
import GenericBanner from './GenericBanner';
import GoalCmsContent from './GoalCmsContent';
import FAQSection from './FAQSection';
import PresubscribedLegendsSection from './PresubscribedLegendsSection';
import UpcomingSection from './UpcomingSection';
import TopicsSection from './TopicsSection';
import TestSeriesSection from './TestSeriesSection';
import SubscriptionsSection from './SubscriptionsSection';
import SubscriptionBenefitsSection from './SubscriptionBenefitsSection';
import ReviewsSection from './ReviewsSection';
import K12LearnOnTheGo from './K12LearnOnTheGo';
import VideoTestimonialsSection from './VideoTestimonialsSection';
import PlatformHeader from './PlatformHeader';
import { UpcomingBatchGroups } from './PFUpcomingSection';
import PFTrialClasses from './PFTrialClasses';
import GenericBannerList from './GenericBannerList';
import DST from './DailyScholarshipTest/DST';
import FreeClassSection from './FreeClassSection';
import HeroBatchSection from './HeroBatchSection';

const ReferralBanner = Dynamic(() =>
  import('@cont/Settings/Referral/ReferralBanner')
);

const sectionIDs = [
  'subscription_top',
  'subscription_benefits',
  'iconic_benefits',
  'lite_benefits',
  'legends_on_unacademy',
  'special_classes',
  'batches_v2',
  'hero_batch',
  'achievement_story',
  'achievement_story_v2',
  'educators',
  'top_educators',
  'test_series',
  'testseries',
  'extra_subscription_benefits',
  'subscriptions',
  'faq',
  'upcoming',
  'courses',
  'app_download',
  'topics',
  'reviews',
  'video_testimonial',
  'batches',
  'generic_banner',
  'carousel_feed',
  'daily_scholarship_test',
  'trial_classes',
  BATCH_GROUP_TYPE.LIVE,
  BATCH_GROUP_TYPE.RECORDED,
  'generic_banner_v2',
  'referral_benefit_card'
];

const Container = styled.div`
  margin-bottom: ${({ $isK12 }) => ($isK12 ? '0' : '160px')};
  ${mediaQuery.uptoTablet} {
    margin-bottom: ${Spacings.SPACING_20B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${({ $isPostCompeteLaunch }) =>
        $isPostCompeteLaunch ? '136px' : Spacings.SPACING_12B}
      ${Spacings.SPACING_0B} ${Spacings.SPACING_10B};
    margin: ${Spacings.SPACING_0B};
    overflow: hidden;
    grid-column: span 10;
  }
`;

const StyledLearn = styled(LearnOnTheGo)`
  margin-top: ${Spacings.SPACING_0B};
`;
const PlatformLearnOnTheGo = styled(LearnOnTheGo)`
  margin: ${Spacings.SPACING_40B} auto;
`;

const BannerWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
  margin-bottom: 124px;
  margin-top: ${Spacings.SPACING_30B};
  ${mediaQuery.uptoTablet} {
    width: 728px;
    margin-bottom: ${Spacings.SPACING_20B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    display: none;
  }
`;

const GenericBannerWrapper = ({ block }) => (
  <BannerWrapper>
    <GenericBanner block={block} />
  </BannerWrapper>
);

GenericBannerWrapper.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired
};

const OneForAllCarousel = ({ block, goalUID }) => {
  const dispatch = useDispatch();
  const enroll = (course) => () =>
    dispatch(toggleEnrolledCourse(course.uid, !course.isEnrolled));
  return (
    <BannerWrapper>
      <CarouselFeedBlock
        goalUID={goalUID}
        block={{
          ...block,
          // Process data for backward compatibility with CarouselFeedBlock component.
          carouselFeed: block.data.map((data) => ({
            course: [
              bannerStates.LEGENDS,
              bannerStates.TEST_SERIES,
              bannerStates.GENERIC,
              bannerStates.COMBAT,
              bannerStates.RENEWAL
            ].includes(data.bannerType)
              ? data.sessionInfo
              : data.sessionInfo?.courseInfo,
            sessionData: data?.sessionInfo,
            bannerDetails: {
              uid: data.uid,
              title: data.title,
              description: data.description,
              type: data.bannerType,
              bannerPhoto: data.bannerPhoto,
              clickURL: data.clickUrl
            }
          }))
        }}
        preSubscribed
        gridColumn="span 24"
        itemWidth={1136}
        itemHeight={340}
        imgWidth="52%"
        leftOverlay="200px"
        enroll={enroll}
      />
    </BannerWrapper>
  );
};

OneForAllCarousel.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired
};

const PreSubscribed = ({
  preSubscribedBlocks,
  goalDetails,
  isSubscribed,
  isK12,
  isPlatformGoal
}) => {
  const router = useRouter();

  const isGoalEnabled = useTTUStore(getTTUIsGoalEnabled);
  const { isMobile } = useWindowSize();
  const { state } = useCompeteConfig();
  const isPostCompeteLaunch = state === COMPETE_STATES.POST_LAUNCH;

  let { goalUID, goalSlug } = router.query;
  goalUID = goalUID.toUpperCase();
  const countryCode = goalUID === 'QZIXN' ? 'BRL' : 'INR';
  const countryName = countryCode === 'INR' ? 'India' : 'Brazil';

  const isPlusSubscribed = useSelector(getIsSubscribed);

  const hasIconicSection = !!preSubscribedBlocks.find(
    (obj) => obj.type === 'iconic_benefits'
  );

  const partPaymentStatus = useSelectorToStore(
    ({ checkout }) => checkout.partPaymentStatus?.[goalUID]
  );

  const learnerAppOneLink = useOneLinkSmartScript(GOAL_PAGE);

  const getHeader = () => {
    if (isPlatformGoal) return PlatformHeader;
    if (isK12) return K12PageHead;
    return PageHead;
  };
  const getLearnOnTheGo = () => {
    if (isPlatformGoal) return PlatformLearnOnTheGo;
    if (isK12) return K12LearnOnTheGo;
    return StyledLearn;
  };
  const getBanner = () => {
    if (isPlatformGoal) return <PlatformTTU goalDetails={goalDetails} />;
    if (isGoalEnabled && !isPlusSubscribed && !isMobile)
      return <TTUBanner goalDetails={goalDetails} />;
    return null;
  };

  const { dstData: dstBlock } = useDst(goalUID, goalDetails?.name, 'Home');

  const renderBlocks = () => {
    const getSection = (id) => {
      if (id === 'subscription_top') return getHeader();
      if (id === 'subscription_benefits') return SecondSection;
      if (id === 'iconic_benefits') return IconicSection;
      if (id === 'lite_benefits') return LiteSection;
      if (id === 'legends_on_unacademy') return PresubscribedLegendsSection;
      if (id === 'special_classes') return FreeClassSection;
      if (id === 'achievement_story') return AchieversCard;
      if (id === 'achievement_story_v2') return NewAchieversCard;

      if (id === 'educators') return FourthSection;
      if (id === 'top_educators') return FourthSection;
      if (id === 'test_series') return TestSeriesSection;
      if (id === 'extra_subscription_benefits')
        return SubscriptionBenefitsSection;
      if (id === 'subscriptions')
        return partPaymentStatus?.is_active_part_set
          ? null
          : SubscriptionsSection;
      if (id === 'faq')
        return partPaymentStatus?.is_active_part_set ? null : FAQSection;
      if (id === 'upcoming') return UpcomingSection;
      if (id === 'courses') return UpcomingSection;
      if (id === 'app_download') return getLearnOnTheGo();
      if (id === 'topics') return TopicsSection;
      if (id === 'reviews') return ReviewsSection;
      if (id === 'video_testimonial') return VideoTestimonialsSection;
      if (id === 'hero_batch') return HeroBatchSection;
      if (id === 'batches_v2') return UpcomingSection;
      if ([BATCH_GROUP_TYPE.LIVE, BATCH_GROUP_TYPE.RECORDED].includes(id))
        return UpcomingBatchGroups;
      if (id === 'generic_banner') return GenericBannerWrapper;
      if (id === 'carousel_feed') return OneForAllCarousel;
      if (id === 'subscription_benefits' && goalUID === 'LEARNCP')
        return IconicSection;
      if (id === 'daily_scholarship_test') return DST;
      if (id === 'trial_classes') return PFTrialClasses;
      if (id === 'generic_banner_v2') return GenericBannerList;
      if (id === 'referral_benefit_card') return ReferralBanner;
      return null;
    };

    const getSections = (type, id) => {
      for (let i = 0; i < sectionIDs.length; i += 1) {
        const key = sectionIDs[i];
        if (
          key.includes(type.toLowerCase()) ||
          (id && key.includes(id.toLowerCase()))
        ) {
          return getSection(key);
        }
      }
    };
    const allBlocks =
      !isEmpty(dstBlock) && preSubscribedBlocks?.length
        ? [
            preSubscribedBlocks[0],
            { ...dstBlock, type: 'daily_scholarship_test' },
            ...preSubscribedBlocks.slice(1)
          ]
        : preSubscribedBlocks;
    return allBlocks?.map((block, i) => {
      const { type, extraBlockInfo, data } = block;
      const { id, tabs = [] } = extraBlockInfo || {};

      const Component = getSections(type, id);
      const tab = (tabs || [])[0];
      const url = tab?.url;
      let hrefURL = null;
      let asURL = null;
      if (url && data?.length > 3) {
        if (type.includes('topic') || id.includes('topic')) {
          hrefURL = '/goal/[goalSlug]/[goalUID]/topics';
          asURL = `/goal/${goalSlug}/${goalUID}/topics`;
        } else if (type.includes('test_series') || id.includes('test_series')) {
          hrefURL = '/goal/[goalSlug]/[goalUID]/test-series';
          asURL = `/goal/${goalSlug}/${goalUID}/test-series`;
        } else if (type.includes('batches')) {
          hrefURL = '/goal/[goalSlug]/[goalUID]/batches';
          asURL = `/goal/${goalSlug}/${goalUID}/batches`;
        } else {
          hrefURL = '/goal/[goalSlug]/[goalUID]/[type]';
          asURL = `/goal/${goalSlug}/${goalUID}/${type}`;
        }
      }
      if ([BATCH_GROUP_TYPE.LIVE, BATCH_GROUP_TYPE.RECORDED].includes(type)) {
        const queryType =
          block?.extraBlockInfo?.classTypeFilter ?? DEFAULT_QUERY_TYPE;
        hrefURL = `/goal/[goalSlug]/[goalUID]/upcoming-batches?type=${queryType}`;
        asURL = `/goal/${goalSlug}/${goalUID}/upcoming-batches?type=${queryType}`;
      } else if (type.includes('trial_classes')) {
        hrefURL = '/goal/[goalSlug]/[goalUID]/trial-classes';
        asURL = `/goal/${goalSlug}/${goalUID}/trial-classes`;
      }

      if (Component)
        return (
          <Component
            key={block.type}
            type={block.type}
            block={block}
            goalUID={goalUID}
            goalSlug={goalSlug}
            goalDetails={goalDetails}
            countryCode={countryCode}
            countryName={countryName}
            href={hrefURL}
            as={asURL}
            dataAnalytics="goal"
            hasIconicSection={hasIconicSection}
            isSubscribed={isSubscribed}
            learnerAppOneLink={learnerAppOneLink}
            blockPosition={i}
          />
        );
      return null;
    });
  };

  return (
    <>
      <OneLinkSmartScript />
      <Container
        id="preSubscribedBlocks"
        $isK12={isK12}
        $isPostCompeteLaunch={isPostCompeteLaunch}
      >
        <CompeteLaunchBanner />
        {renderBlocks()}
        {getBanner()}
        <GoalCmsContent goalUID={goalUID} />
        <AppInstallBanner pageType="freeHomePage" goal={goalDetails} />
      </Container>
    </>
  );
};

export default PreSubscribed;

PreSubscribed.propTypes = {
  preSubscribedBlocks: PropTypes.arrayOf(PropTypes.any).isRequired,
  isSubscribed: PropTypes.bool.isRequired,
  isK12: PropTypes.bool.isRequired
};
